// src/shared/utils/gaugeUtils.js

/**
 * Gauge-Aware Length Calculation Utilities
 * 
 * Transforms "work until X inches" from guesswork into intelligent, 
 * gauge-aware progress tracking with adaptive learning.
 */

// ===== STEP TYPE DETECTION =====

/**
 * Determine if a step is length-based
 */
export const isLengthBasedStep = (step) => {
    const duration = step.wizardConfig?.duration;
    return duration?.type === 'length' || duration?.type === 'until_length';
};

/**
 * Get length target information from step
 */
export const getLengthTarget = (step) => {
    const duration = step.wizardConfig?.duration;

    if (!isLengthBasedStep(step)) {
        return null;
    }

    return {
        value: parseFloat(duration.value) || 0,
        units: duration.units || 'inches',
        type: duration.type // 'length' or 'until_length'
    };
};

// ===== GAUGE CALCULATIONS =====

/**
 * Get row gauge from project in rows per unit (native project units)
 * Returns rows per inch if project uses inches, rows per cm if project uses cm
 */
export const getRowGaugePerInch = (project) => {
    const rowGauge = project?.gauge?.rowGauge;

    if (!rowGauge?.rows) return null;

    const rows = parseFloat(rowGauge.rows);
    const measurement = parseFloat(rowGauge.measurement) || 4;

    // Return rows per measurement unit (no conversion needed!)
    return rows / measurement; // rows per unit (inch or cm)
};

/**
 * Convert length to estimated rows using project gauge
 * Works in native project units - no conversion needed!
 */
export const estimateRowsFromLength = (targetLength, targetUnits, project) => {
    const rowGaugePerUnit = getRowGaugePerInch(project); // Actually returns rows per unit now

    if (!rowGaugePerUnit) return null;

    // Both gauge and target should be in same units (project.defaultUnits)
    // If they're not, convert target to match gauge units
    const projectUnits = project?.defaultUnits || 'inches';
    let targetInProjectUnits = targetLength;

    if (targetUnits !== projectUnits) {
        // Convert between cm and inches if needed
        if (targetUnits === 'cm' && projectUnits === 'inches') {
            targetInProjectUnits = targetLength / 2.54;
        } else if (targetUnits === 'inches' && projectUnits === 'cm') {
            targetInProjectUnits = targetLength * 2.54;
        }
    }

    return Math.round(targetInProjectUnits * rowGaugePerUnit);
};

/**
 * Convert current row to estimated length using project gauge  
 */
export const estimateLengthFromRows = (currentRow, estimatedTotalRows, targetLength, targetUnits) => {
    if (!estimatedTotalRows || estimatedTotalRows === 0) return 0;

    const progress = currentRow / estimatedTotalRows;
    return targetLength * progress;
};

/**
 * Calculate progress percentage for length-based steps
 */
export const calculateLengthProgress = (currentRow, estimatedTotalRows, targetLength) => {
    if (!estimatedTotalRows || estimatedTotalRows === 0) return 0;

    return Math.min((currentRow / estimatedTotalRows) * 100, 100);
};

// ===== DISPLAY UTILITIES =====

/**
 * Get gauge-aware display text for length-based steps
 * FIXED: Now correctly calculates progress for until_length steps
 */
export const getLengthProgressDisplay = (step, currentRow, project, startingLength = null) => {
    const lengthTarget = getLengthTarget(step);
    if (!lengthTarget) return null;

    // Handle until-length steps by converting to equivalent length step
    let effectiveTargetLength = lengthTarget.value;
    let effectiveTargetUnits = lengthTarget.units;

    if (lengthTarget.type === 'until_length' && startingLength !== null) {
        effectiveTargetLength = lengthTarget.value - startingLength;

        if (effectiveTargetLength <= 0) {
            // Already at or past target
            return {
                hasGauge: false,
                targetLength: lengthTarget.value,
                targetUnits: lengthTarget.units,
                currentRow,
                alreadyComplete: true,
                showEstimate: false
            };
        }
    }

    const estimatedRows = estimateRowsFromLength(effectiveTargetLength, effectiveTargetUnits, project);
    if (!estimatedRows) {
        // No gauge available - show basic info
        return {
            hasGauge: false,
            targetLength: lengthTarget.value,
            targetUnits: lengthTarget.units,
            currentRow,
            showEstimate: false
        };
    }

    // 🔧 FIXED: Use effectiveTargetLength for calculations
    const currentLength = estimateLengthFromRows(currentRow, estimatedRows, effectiveTargetLength, effectiveTargetUnits);
    const progressPercent = calculateLengthProgress(currentRow, estimatedRows, effectiveTargetLength);

    // 🔧 FIXED: For until_length steps, add starting length to current progress for display
    let displayCurrentLength = currentLength;
    if (lengthTarget.type === 'until_length' && startingLength !== null) {
        displayCurrentLength = startingLength + currentLength;
    }

    return {
        hasGauge: true,
        targetLength: lengthTarget.value,
        targetUnits: lengthTarget.units,
        estimatedRows,
        currentRow,
        currentLength: Math.round(displayCurrentLength * 10) / 10, // 1 decimal place
        progressPercent: Math.round(progressPercent),
        showEstimate: true,
        isNearTarget: currentRow >= estimatedRows * 0.9, // 90% threshold
        hasReachedEstimate: currentRow >= estimatedRows,
        shouldShowNearAlert: currentRow === Math.ceil(estimatedRows * 0.9),
        shouldShowTargetAlert: currentRow === estimatedRows
    };
};

/**
 * Format length progress for counter display
 */
export const formatLengthCounterDisplay = (progressData, construction) => {
    if (!progressData) return null;

    const rowTerm = construction === 'round' ? 'Round' : 'Row';

    if (!progressData.showEstimate) {
        // No gauge - basic display
        return {
            rowText: `${rowTerm} ${progressData.currentRow}`,
            progressText: `Target: ${progressData.targetLength} ${progressData.targetUnits}`,
            showProgressBar: false
        };
    }

    // With gauge - intelligent display
    const lengthText = `~${progressData.currentLength} ${progressData.targetUnits}`;
    const targetText = `${progressData.targetLength} ${progressData.targetUnits}`;

    return {
        rowText: `${rowTerm} ${progressData.currentRow}`,
        progressText: `${lengthText} of ${targetText} (${progressData.progressPercent}%)`,
        showProgressBar: true,
        progressPercent: progressData.progressPercent,
        isNearTarget: progressData.isNearTarget,
        hasReachedEstimate: progressData.hasReachedEstimate
    };
};

/**
 * Format length progress for modal header
 */
export const formatLengthHeaderDisplay = (step, currentRow, project, construction) => {
    const progressData = getLengthProgressDisplay(step, currentRow, project);
    if (!progressData) return null;

    const rowTerm = construction === 'round' ? 'Round' : 'Row';

    if (!progressData.showEstimate) {
        return `${rowTerm} ${currentRow} • ${progressData.targetLength} ${progressData.targetUnits} target`;
    }

    return `${rowTerm} ${currentRow} • ~${progressData.currentLength}" of ${progressData.targetLength}" target`;
};

// ===== COMPLETION SUGGESTIONS =====

/**
 * Determine if step should show completion suggestion
 */
export const shouldSuggestCompletion = (step, currentRow, project) => {
    const progressData = getLengthProgressDisplay(step, currentRow, project);
    if (!progressData?.showEstimate) return false;

    return progressData.hasReachedEstimate;
};

/**
 * Get completion suggestion text
 */
export const getCompletionSuggestionText = (step, currentRow, project) => {
    const lengthTarget = getLengthTarget(step);
    if (!lengthTarget) return '';

    return `🎯 You've likely reached ${lengthTarget.value} ${lengthTarget.units}. Measure to confirm!`;
};

// ===== ADAPTIVE LEARNING =====

/**
 * Calculate measured gauge from completed length step
 * Returns gauge in rows per unit (same units as targetUnits)
 */
export const calculateMeasuredGauge = (actualRows, targetLength, targetUnits, step, startingLength = null) => {
    // Calculate the effective distance that was actually knitted
    let effectiveDistance = targetLength;

    // For until_length steps, use only the distance actually knitted
    if (step) {
        const lengthTarget = getLengthTarget(step);
        if (lengthTarget?.type === 'until_length' && startingLength !== null) {
            effectiveDistance = targetLength - startingLength;

            // Safety check - if no effective distance, can't calculate gauge
            if (effectiveDistance <= 0) {
                return null;
            }
        }
    }

    // Return gauge in native units - no conversion!
    return actualRows / effectiveDistance; // rows per unit (cm or inches)
};

/**
 * ✅ FIXED: Check if step completion should prompt for gauge update
 * Now always returns true for length steps - we show gauge card every time
 * But we mark whether the gauge is a "match" or needs updating
 */
export const shouldPromptGaugeUpdate = (step, actualRows, project, startingLength = null) => {
    const lengthTarget = getLengthTarget(step);
    if (!lengthTarget || !actualRows) return false;

    const measuredGauge = calculateMeasuredGauge(actualRows, lengthTarget.value, lengthTarget.units, step, startingLength);
    if (!measuredGauge) return false; // Can't calculate gauge

    // ✅ CHANGE: Always show gauge card for length steps
    return true;
};

/**
 * ✅ FIXED: Get gauge update prompt data for user display
 * Now includes an "isMatch" flag to indicate if gauge is within tolerance
 */
export const getGaugeUpdatePromptData = (actualRows, step, project, startingLength = null) => {
    const lengthTarget = getLengthTarget(step);
    if (!lengthTarget) return null;

    const measuredGauge = calculateMeasuredGauge(actualRows, lengthTarget.value, lengthTarget.units, step, startingLength);
    if (!measuredGauge) return null; // Can't calculate gauge

    const currentGauge = getRowGaugePerInch(project);

    // Convert back to project's measurement format (4" or 10cm)
    const gaugeMeasurement = project?.gauge?.rowGauge?.measurement || 4;
    const newRowsForMeasurement = Math.round(measuredGauge * gaugeMeasurement);

    // Calculate what distance was actually knitted for display
    let actualDistance = lengthTarget.value;
    if (lengthTarget.type === 'until_length' && startingLength !== null) {
        actualDistance = lengthTarget.value - startingLength;
    }

    // ✅ NEW: Calculate if gauges match (within 5% tolerance)
    let isMatch = false;
    let percentDifference = 0;
    
    if (currentGauge) {
        percentDifference = Math.abs((measuredGauge - currentGauge) / currentGauge) * 100;
        isMatch = percentDifference <= 5; // Within 5% is considered a match
    }

    return {
        measuredGauge: Math.round(measuredGauge * 10) / 10, // 1 decimal
        newRowsForMeasurement,
        measurement: gaugeMeasurement,
        units: project?.defaultUnits === 'cm' ? 'cm' : 'inches',
        hasExistingGauge: !!currentGauge,
        oldRowsForMeasurement: currentGauge ? Math.round(currentGauge * gaugeMeasurement) : null,
        actualDistance: Math.round(actualDistance * 10) / 10, // Show what was actually knitted
        actualRows: actualRows,
        // ✅ NEW: Add match information
        isMatch: isMatch,
        percentDifference: Math.round(percentDifference * 10) / 10 // 1 decimal place
    };
};

/**
 * Update project gauge with measured data
 */
export const updateProjectGaugeFromMeasurement = (project, gaugeData) => {
    return {
        ...project,
        gauge: {
            ...project.gauge,
            rowGauge: {
                rows: gaugeData.newRowsForMeasurement,
                measurement: gaugeData.measurement
            }
        }
    };
};

// ===== VALIDATION UTILITIES =====

/**
 * Check if project has sufficient gauge data for length calculations
 */
export const hasValidGaugeForLength = (project) => {
    const rowGauge = project?.gauge?.rowGauge;
    return !!(rowGauge?.rows && parseFloat(rowGauge.rows) > 0);
};

/**
 * Get gauge availability message for user
 */
export const getGaugeAvailabilityMessage = (project) => {
    if (hasValidGaugeForLength(project)) {
        return {
            hasGauge: true,
            message: `Using gauge: ${project.gauge.rowGauge.rows} rows = ${project.gauge.rowGauge.measurement || 4} inches`
        };
    }

    return {
        hasGauge: false,
        message: 'Add row gauge to your project for intelligent length tracking!'
    };
};

// ===== STEP DESCRIPTION FIXES =====

/**
 * Get corrected duration display for length steps
 * Fixes the issue where "2 inches" shows as "2 rows"
 * ✅ UPDATED: Now handles target_repeats duration type
 */
export const getCorrectDurationDisplay = (step, project) => {
    const duration = step.wizardConfig?.duration;
    const construction = step.construction || 'flat';
    const rowTerm = construction === 'round' ? 'rounds' : 'rows';

    if (!duration?.type) {
        return step.totalRows ? `${step.totalRows} ${rowTerm}` : null;
    }

    // Handle length-based steps correctly
    if (duration.type === 'length') {
        const estimatedRows = estimateRowsFromLength(
            parseFloat(duration.value) || 0,
            duration.units || 'inches',
            project
        );

        if (estimatedRows) {
            return `${duration.value} ${duration.units} (~${estimatedRows} ${rowTerm})`;
        }

        return `${duration.value} ${duration.units}`;
    }

    if (duration.type === 'until_length') {
        const referenceText = duration.reference ? ` from ${duration.reference}` : '';
        return `until ${duration.value} ${duration.units}${referenceText}`;
    }

    // ✅ NEW: Handle target-based repeats
    if (duration.type === 'target_repeats') {
        const targetStitches = duration.targetStitches;
        const stitchChangePerRepeat = step.wizardConfig?.stitchPattern?.stitchChangePerRepeat;
        const startingStitches = step.startingStitches;

        if (stitchChangePerRepeat && startingStitches != null) {
            const stitchDifference = targetStitches - startingStitches;
            const repeatsNeeded = Math.ceil(Math.abs(stitchDifference) / Math.abs(stitchChangePerRepeat));

            return `${repeatsNeeded} ${repeatsNeeded === 1 ? 'repeat' : 'repeats'}`;
        }

        return `until ${targetStitches} stitches`;  // Fallback
    }

    // Handle other types normally
    switch (duration.type) {
        case 'rows':
        case 'rounds':
            return `${duration.value} ${rowTerm}`;

        case 'repeats':
            const patternName = step.wizardConfig?.stitchPattern?.pattern || 'pattern';
            // Don't include pattern name for brioche since it's in the description already
            if (patternName === 'Two-Color Brioche' || patternName === 'Brioche') {
                return `${duration.value} ${duration.value === 1 ? 'repeat' : 'repeats'}`;
            }
            return `${duration.value} ${patternName} ${duration.value === 1 ? 'repeat' : 'repeats'}`;

        case 'color_repeats':
            // Get colorwork type from step
            const colorworkType = step.colorwork?.type ||
                step.wizardConfig?.colorwork?.type ||
                step.wizardConfig?.colorwork?.advancedType ||
                'color';

            // Capitalize and make singular for better grammar
            let colorTypeName = colorworkType.charAt(0).toUpperCase() + colorworkType.slice(1);

            // Remove trailing 's' if it exists (Stripes -> Stripe)
            if (colorTypeName.endsWith('s')) {
                colorTypeName = colorTypeName.slice(0, -1);
            }

            return `${duration.value} ${colorTypeName} repeats`;

        case 'stitches':
            return `${duration.value || 'all'} stitches`;

        default:
            return null;
    }
};
/**
 * Calculate rows from distance for marker timing with construction awareness
 * @param {number} distance - Distance value
 * @param {object} project - Project with gauge data
 * @param {string} construction - 'flat' or 'round' 
 * @returns {object} - { estimatedRows: number | null, hasGauge: boolean }
 */
export const calculateRowsFromDistance = (distance, project, construction = 'flat') => {
    const estimatedRows = estimateRowsFromLength(
        distance,
        project?.defaultUnits || 'inches',
        project
    );

    if (!estimatedRows) {
        return { estimatedRows: null, hasGauge: false };
    }

    // For flat construction, ensure odd number (shaping ends on RS)
    let adjustedRows = estimatedRows;
    if (construction === 'flat' && estimatedRows % 2 === 0) {
        // Round even numbers up to next odd for flat construction
        adjustedRows = estimatedRows + 1;
    }

    return { estimatedRows: adjustedRows, hasGauge: true };
};
